import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Plus, UserPlus } from 'lucide-react';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useSession } from '@/core/auth/useSession';
import { useAnonimizarCliente, useClientes, useDesactivarCliente } from '@/data/hooks/useClientes';
import { formatearFecha } from '@/core/utils/formatters';
import { AppError } from '@/core/errors/AppError';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { Pagination } from '@/shared/components/Pagination';
import { SearchInput } from '@/shared/components/SearchInput';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { useToast } from '@/shared/components/Toast';
import { ClienteDetalleDialog } from './ClienteDetalleDialog';
import { ClienteFormDialog } from './ClienteFormDialog';
const POR_PAGINA = 20;
export function ClientesPage() {
    const { tieneRol } = useSession();
    const puedeAdmin = tieneRol('ROLE_ADMINISTRAR_CLIENTE');
    const toast = useToast();
    const [busqueda, setBusqueda] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('activos');
    const [pagina, setPagina] = useState(1);
    const q = useDebounce(busqueda, 300);
    const { data, isLoading, isError, error, refetch } = useClientes({
        q,
        activo: estadoFiltro === 'todos' ? undefined : estadoFiltro === 'activos',
        pagina,
        porPagina: POR_PAGINA,
    });
    const [formOpen, setFormOpen] = useState(false);
    const [clienteEditable, setClienteEditable] = useState(null);
    const [detalleOpen, setDetalleOpen] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [confirmDesactivar, setConfirmDesactivar] = useState(null);
    const [confirmAnonimizar, setConfirmAnonimizar] = useState(null);
    const desactivar = useDesactivarCliente();
    const anonimizar = useAnonimizarCliente();
    const columnas = useMemo(() => [
        {
            key: 'nombre',
            header: 'Cliente',
            cell: (c) => (_jsxs("div", { className: "flex flex-col", children: [_jsxs("span", { className: "font-medium", children: [c.nombre, c.apellido ? ` ${c.apellido}` : '', c.anonimizado ? (_jsx(Chip, { tono: "warning", className: "ml-2", children: "Anonimizado" })) : null] }), _jsx("span", { className: "text-xs text-text-tertiary", children: c.email ?? '—' })] })),
        },
        {
            key: 'documento',
            header: 'Documento',
            cell: (c) => c.tipoDocumento || c.nroDocumento
                ? `${c.tipoDocumento ?? ''} ${c.nroDocumento ?? ''}`.trim()
                : '—',
            width: '200px',
        },
        {
            key: 'telefono',
            header: 'Teléfono',
            cell: (c) => c.telefono ?? '—',
            width: '160px',
        },
        {
            key: 'fechaAlta',
            header: 'Alta',
            cell: (c) => formatearFecha(c.creadoEn),
            width: '120px',
        },
        {
            key: 'estado',
            header: 'Estado',
            width: '120px',
            cell: (c) => c.activo ? _jsx(Chip, { tono: "success", children: "Activo" }) : _jsx(Chip, { tono: "neutral", children: "Inactivo" }),
        },
    ], []);
    const onRowClick = (c) => {
        setClienteSeleccionado(c);
        setDetalleOpen(true);
    };
    const abrirNuevo = () => {
        setClienteEditable(null);
        setFormOpen(true);
    };
    const abrirEdicion = (c) => {
        setClienteEditable(c);
        setDetalleOpen(false);
        setFormOpen(true);
    };
    const ejecutarDesactivar = (c) => {
        desactivar.mutate(c.id, {
            onSuccess: () => {
                toast.exito('Cliente desactivado');
                setConfirmDesactivar(null);
                setDetalleOpen(false);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos desactivarlo.'),
        });
    };
    const ejecutarAnonimizar = (c) => {
        anonimizar.mutate(c.id, {
            onSuccess: (actualizado) => {
                toast.exito('Cliente anonimizado');
                setConfirmAnonimizar(null);
                setClienteSeleccionado(actualizado);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos anonimizarlo.'),
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-5", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-text-primary", children: "Clientes" }), _jsx("p", { className: "text-sm text-text-secondary", children: "Gestion\u00E1 los clientes registrados en el sistema." })] }), puedeAdmin ? (_jsxs(Button, { onClick: abrirNuevo, children: [_jsx(Plus, { className: "h-4 w-4" }), "Nuevo cliente"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(SearchInput, { value: busqueda, onChange: (v) => {
                            setBusqueda(v);
                            setPagina(1);
                        }, placeholder: "Buscar por nombre, email o documento\u2026" }), _jsxs(Select, { value: estadoFiltro, onChange: (e) => {
                            setEstadoFiltro(e.target.value);
                            setPagina(1);
                        }, className: "w-40", children: [_jsx("option", { value: "activos", children: "Activos" }), _jsx("option", { value: "inactivos", children: "Inactivos" }), _jsx("option", { value: "todos", children: "Todos" })] })] }), isError ? (_jsxs("div", { className: "rounded-lg border border-danger bg-danger-fill px-4 py-3 text-sm text-danger", children: [error instanceof AppError ? error.mensaje : 'No pudimos cargar los clientes.', ' ', _jsx("button", { onClick: () => refetch(), className: "ml-2 underline", children: "Reintentar" })] })) : (_jsxs(_Fragment, { children: [_jsx(Table, { columns: columnas, items: data?.items ?? [], rowKey: (c) => c.id, onRowClick: onRowClick, cargando: isLoading, vacioTitulo: "Sin clientes", vacioDescripcion: q ? 'Probá con otro término de búsqueda.' : 'Aún no hay clientes cargados.', vacioAccion: puedeAdmin && !q ? (_jsxs(Button, { onClick: abrirNuevo, children: [_jsx(UserPlus, { className: "h-4 w-4" }), "Crear el primero"] })) : null }), data ? (_jsx(Pagination, { pagina: data.paginacion.pagina, totalPaginas: data.paginacion.totalPaginas, total: data.paginacion.total, porPagina: data.paginacion.porPagina, onCambioPagina: setPagina })) : null] })), _jsx(ClienteFormDialog, { open: formOpen, onOpenChange: setFormOpen, cliente: clienteEditable }), _jsx(ClienteDetalleDialog, { open: detalleOpen, onOpenChange: setDetalleOpen, cliente: clienteSeleccionado, onEditar: () => clienteSeleccionado && abrirEdicion(clienteSeleccionado), onDesactivar: () => clienteSeleccionado && setConfirmDesactivar(clienteSeleccionado), onAnonimizar: () => clienteSeleccionado && setConfirmAnonimizar(clienteSeleccionado) }), _jsx(ConfirmDialog, { open: Boolean(confirmDesactivar), onOpenChange: (open) => !open && setConfirmDesactivar(null), titulo: "Desactivar cliente", descripcion: confirmDesactivar
                    ? `El cliente "${confirmDesactivar.nombre}${confirmDesactivar.apellido ? ' ' + confirmDesactivar.apellido : ''}" dejará de aparecer en los listados activos. Podés reactivarlo desde el filtro "Inactivos".`
                    : undefined, textoConfirmar: "Desactivar", destructivo: true, cargando: desactivar.isPending, onConfirmar: () => confirmDesactivar && ejecutarDesactivar(confirmDesactivar) }), _jsx(ConfirmDialog, { open: Boolean(confirmAnonimizar), onOpenChange: (open) => !open && setConfirmAnonimizar(null), titulo: "Anonimizar cliente", descripcion: "Esta acci\u00F3n elimina datos personales de forma irreversible (Ley 25.326). El cliente seguir\u00E1 visible en el hist\u00F3rico con informaci\u00F3n anonimizada.", textoConfirmar: "Anonimizar", destructivo: true, cargando: anonimizar.isPending, onConfirmar: () => confirmAnonimizar && ejecutarAnonimizar(confirmAnonimizar) })] }));
}
